import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Rating } from 'primereact/rating';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { Button } from 'primereact/button';
import Strings from '../../../../i18n';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarSimpleMethodItem } from '../../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { SideBarDeleteActionWithPopup } from '../../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import { IEffectItemStore } from '../../../../types/effects/effectsStoreInterface';
import { SideBarSimpleMethodWithValue } from '../../../../components/commons/SideBarComponents/MethodWithValueItem/sideBarMethodWithValueItem';
import {
  useBanUserMutation,
  useDeleteEffectMutation,
  useEffectRatingMutation,
  useGetEffectRatingsMutation,
  usePutChangeEffectStatusMutation,
  usePutChangeVideoEffectMutation,
  useUpdateEffectMutation
} from '../../../../services/effects/effectsStoreService';
import {
  StyledEffectVote,
  StyledRateContainer
} from '../effectStoreItem/effectStoreItemStyles';
import { viewRightSideBarEnum } from '../../../../constants/sidebarEnums';
import { colors } from '../../../../constants/colors';
import { EFFECTS_STATUS } from '../../../../constants/effectsEnums';
import { SideBarReadOnly } from '../../../../components/commons/SideBarComponents/ReadOnlyItems/sideBarReadOnly';

interface SingleDetailsEffectStoreProps {
  effect: IEffectItemStore;
  closeSideBar: () => void;
  reloadTable: () => void;
  reloadTableWithoutRemoveSelectedEffects: () => void;
  setOpenShareDialog: (value: boolean) => void;
  setOpenReportDialog: (value: boolean) => void;
  openSideBarFor: (value: viewRightSideBarEnum) => void;
}
export const SingleDetailsEffectStore = ({
  effect,
  closeSideBar,
  reloadTable,
  reloadTableWithoutRemoveSelectedEffects,
  setOpenShareDialog,
  setOpenReportDialog,
  openSideBarFor
}: SingleDetailsEffectStoreProps) => {
  const [fileUpload, setFileUpload] = useState<any>(null);

  const [isVoting, setIsVoting] = useState<boolean>(false);

  const [confirmationInfo, setConfirmationInfo] = useState<{
    status: number;
    deniedReason: string | null;
  }>({
    status: 2,
    deniedReason: null
  });

  const [openDialogVideo, setOpenDialogVideo] = useState<boolean>(false);

  const [newRating, setNewRating] = useState<number>(
    effect?.ratingFromUser ?? 0
  );

  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);

  const [rateEffect, { isSuccess: successRateEffect }] =
    useEffectRatingMutation();

  const [deleteEffects, { isSuccess: successDelete }] =
    useDeleteEffectMutation();

  const [updateEffect, { isSuccess: successUpdate }] =
    useUpdateEffectMutation();

  const [
    updateVideoEffect,
    {
      isSuccess: successUpdateVideo,
      isLoading: isLoadingUpdateVideo,
      isError: isErrorUpdateVideo,
      error: errorUpdateVideo
    }
  ] = usePutChangeVideoEffectMutation();

  const [
    changeStatus,
    { isSuccess: successChangeStatus, isLoading: isLoadingChangeStatus }
  ] = usePutChangeEffectStatusMutation();

  const [banUserMethod, { isSuccess: successBanUser }] = useBanUserMutation();

  const deleteEffect = () => {
    deleteEffects([effect.id]);
  };

  const shareEffect = () => {
    setOpenShareDialog(true);
    closeSideBar();
  };
  const reportEffect = () => {
    setOpenReportDialog(true);
    closeSideBar();
  };

  const handleRatingChange = (e: { value: number }) => {
    setNewRating(e.value);
    setConfirmDialog(true);
  };

  const handleConfirmRating = () => {
    rateEffect({ effectId: effect.id, rating: newRating });
    setIsVoting(false);
    setConfirmDialog(false);
  };

  const handleCancelRating = () => {
    setIsVoting(false);
    setConfirmDialog(false);
  };

  const [name, setName] = React.useState<string>(effect?.name ?? '');

  const updateEffectName = () => {
    updateEffect({
      effectId: effect.id,
      name,
      tags: effect.tags,
      isHaven: effect.isHaven
    });
  };

  const handleVoteClick = () => {
    setIsVoting(true);
  };

  const changeFileHandler = (event: any) => {
    setFileUpload(event.target.files[0]);
  };

  const changeVideo = () => {
    const formData = new FormData();
    formData.append('file', fileUpload);
    updateVideoEffect({ effectId: effect.id, video: formData });
  };

  const handleCloseDialogVideo = () => {
    setOpenDialogVideo(false);
  };

  useEffect(() => {
    if (successDelete) {
      reloadTable();
      closeSideBar();
      toast.success(Strings.effects.deleteEffectSuccess);
    }
  }, [successDelete]);

  useEffect(() => {
    if (successUpdate) {
      toast.success(Strings.effects.updateEffectSuccess);
      reloadTableWithoutRemoveSelectedEffects();
    }
  }, [successUpdate]);

  useEffect(() => {
    if (successRateEffect) {
      toast.success(Strings.effects.rateEffectSuccess);
      reloadTable();
    }
  }, [successRateEffect]);

  useEffect(() => {
    if (successUpdateVideo) {
      toast.success(Strings.effects.changeVideoSuccess);
      reloadTable();
      setFileUpload(null);
    }
  }, [successUpdateVideo]);

  useEffect(() => {
    if (isErrorUpdateVideo) {
      toast.error(
        `${(errorUpdateVideo as { data: { message: string } }).data.message}`
      );
    }
  }, [isErrorUpdateVideo]);
  const banUser = () => {
    banUserMethod({ effectId: effect.id, banned: true });
  };

  const handleChangeStatus = (status: EFFECTS_STATUS) => {
    if (
      confirmationInfo.status === EFFECTS_STATUS.DENIED &&
      !confirmationInfo.deniedReason
    ) {
      toast.error(Strings.effects.deniedReasonValidationMessage);
      return;
    }
    changeStatus({
      status,
      deniedReason: confirmationInfo.deniedReason,
      effectId: effect.id
    });
  };

  useEffect(() => {
    if (successBanUser) {
      toast.success(Strings.effects.banUserSuccess);
      reloadTable();
    }
  }, [successBanUser]);

  useEffect(() => {
    if (successChangeStatus) {
      toast.success(Strings.effects.changeStatusSuccess);
      confirmationInfo.deniedReason = null;
      reloadTable();
      closeSideBar();
    }
  }, [successChangeStatus]);

  return (
    <>
      <StyledContainerItems>
        <StyledTitleSideBar>{effect?.name}</StyledTitleSideBar>
        <SideBarSimpleMethodWithValue
          label={Strings.common.name}
          type="inputText"
          currentValue={name}
          action={() => {
            updateEffectName();
          }}
          onChange={(e) => {
            setName(e.target.value);
          }}
          successAction={false}
          textSuccess="success"
          buttonLabel="Change"
        />
        <SideBarReadOnly
          readOnlyItems={[
            { name: Strings.effects.description, value: effect?.description }
          ]}
        />
        <SideBarSimpleMethodWithValue
          loading={isLoadingUpdateVideo}
          label="Change video"
          type="inputFile"
          currentValue={fileUpload}
          fileName={fileUpload?.name ?? ''}
          onChange={changeFileHandler}
          acceptedFiles="video/*"
          action={() => {
            changeVideo();
          }}
          textSuccess="success"
          buttonLabel="Change"
          successAction={false}
        />
        {isVoting ? (
          <StyledRateContainer className="d-block text-center">
            <Rating
              value={newRating || 0}
              stars={5}
              cancel={false}
              onChange={(event: any) => handleRatingChange(event)}
              className="justify-content-center"
            />
            <StyledEffectVote
              className="p-button-sm"
              onClick={handleCancelRating}
              type="button"
            >
              {Strings.common.cancel}
            </StyledEffectVote>
          </StyledRateContainer>
        ) : (
          <StyledRateContainer className="d-block text-center">
            <Rating
              value={effect?.rating}
              stars={5}
              cancel={false}
              readOnly
              className="justify-content-center"
            />
            <StyledEffectVote
              className="p-button-sm"
              onClick={handleVoteClick}
              type="button"
            >
              {Strings.effects.rate}
            </StyledEffectVote>
          </StyledRateContainer>
        )}
        {effect?.publishingStatus !== EFFECTS_STATUS.APPROVED && (
          <SideBarSimpleMethodItem
            name={Strings.effects.approve}
            action={() => {
              handleChangeStatus(EFFECTS_STATUS.APPROVED);
            }}
            successAction={false}
            textSuccess="success"
            colorText={colors.green}
          />
        )}
        <SideBarSimpleMethodItem
          name={
            effect?.isHaven
              ? Strings.effects.removeAsHavenEffect
              : Strings.effects.setAsHavenEffect
          }
          action={() => {
            updateEffect({
              effectId: effect.id,
              isHaven: !effect.isHaven,
              name: effect.name,
              tags: effect.tags
            });
          }}
          successAction={false}
          textSuccess="success"
        />
        <SideBarSimpleMethodItem
          name={Strings.effects.testEffect}
          action={() => {
            openSideBarFor(viewRightSideBarEnum.testEffect);
          }}
          successAction={false}
          textSuccess="success"
        />
        {effect?.publishingStatus === EFFECTS_STATUS.APPROVED && (
          <SideBarSimpleMethodItem
            name={Strings.effects.viewRates}
            action={() => {
              openSideBarFor(viewRightSideBarEnum.viewRates);
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        {effect?.publishingStatus === EFFECTS_STATUS.APPROVED && (
          <SideBarSimpleMethodItem
            name={Strings.effects.shareEffect}
            action={() => {
              shareEffect();
            }}
            successAction={false}
            textSuccess="success"
          />
        )}
        <SideBarSimpleMethodItem
          name={Strings.effects.viewVideo}
          action={() => {
            setOpenDialogVideo(true);
          }}
          successAction={false}
          textSuccess="success"
        />
        <SideBarSimpleMethodItem
          name={Strings.effects.reportEffect}
          action={() => {
            reportEffect();
          }}
          successAction={false}
          textSuccess="success"
        />
        <SideBarSimpleMethodWithValue
          textColor={colors.red}
          label="Reason *REQUIRED"
          type="inputText"
          currentValue={confirmationInfo.deniedReason}
          fileName={fileUpload?.name ?? ''}
          onChange={(e) => {
            setConfirmationInfo({
              ...confirmationInfo,
              deniedReason: e.target.value
            });
          }}
          action={() => {
            handleChangeStatus(EFFECTS_STATUS.DENIED);
          }}
          loading={isLoadingChangeStatus}
          textSuccess="success"
          buttonLabel={Strings.effects.deny}
          successAction={false}
        />
        <SideBarDeleteActionWithPopup
          onDelete={() => banUser()}
          titleConfirmation={Strings.effects.banUser}
          textConfirmation={Strings.common.thisActionWillBanUser}
          textIcon={Strings.effects.banUser}
        />
        <SideBarDeleteActionWithPopup
          onDelete={() => deleteEffect()}
          titleConfirmation={Strings.effects.deleteEffect}
          textConfirmation={Strings.common.thisActionIsIrreversible}
          textIcon={Strings.effects.deleteEffect}
        />
      </StyledContainerItems>
      <StyledContainerFooterSideBar>
        <button
          type="button"
          className="btn-cancel"
          onClick={() => {
            closeSideBar();
            setFileUpload(null);
            confirmationInfo.deniedReason = null;
          }}
        >
          {Strings.common.cancel}
        </button>
      </StyledContainerFooterSideBar>
      <Dialog open={confirmDialog} onClose={handleCancelRating}>
        <DialogTitle>{Strings.effects.confirmRatingTitle}</DialogTitle>
        <DialogContent>
          <p>
            {Strings.effects.willGiveRating} {newRating} {Strings.effects.stars}
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmRating} color="primary">
            {Strings.common.yes}
          </Button>
          <Button onClick={handleCancelRating} color="secondary">
            {Strings.common.cancel}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDialogVideo} onClose={handleCloseDialogVideo}>
        <DialogTitle>{effect?.name}</DialogTitle>
        <DialogContent>
          <video style={{ maxWidth: '340px' }} controls autoPlay loop>
            <source src={effect?.videoUrl} type="video/mp4" />
            <track kind="captions" src="" label="English" default />
            {Strings.common.videoNotSupported}
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogVideo} color="info">
            {Strings.common.cancel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
